import type { CSSProperties, ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { useClickAway } from 'react-use';

import styles from './DynamicTooltip.module.scss';

export type ArrowPosition = 'top' | 'bottom' | 'left' | 'right';
export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

const PORTAL_GAP_PX = 12;

interface DynamicTooltipProps {
  children: ReactNode;
  isOpen: boolean;
  targetRef?: React.RefObject<HTMLElement>;
  position?: TooltipPosition;
  arrowPosition?: ArrowPosition;
  arrowAlign?: 'start' | 'center' | 'end' | 'dynamic';
  className?: string;
  contentClassName?: string;
  minWidth?: number;
  tooltipClassName?: string;
  onClose?: () => void;
  usePortal?: boolean;
}

export const DynamicTooltip: React.FC<DynamicTooltipProps> = ({
  children,
  isOpen,
  targetRef,
  position = 'bottom',
  arrowPosition = 'top',
  arrowAlign = 'center',
  className,
  contentClassName,
  tooltipClassName,
  minWidth,
  onClose,
  usePortal = false,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [arrowOffset, setArrowOffset] = useState<number | undefined>(undefined);
  const [fixedPosition, setFixedPosition] = useState<CSSProperties | undefined>(undefined);

  useClickAway(tooltipRef, () => {
    setIsVisible(false);
    onClose?.();
  });

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (arrowAlign === 'dynamic' && targetRef?.current && tooltipRef.current) {
      const targetRect = targetRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const minEdgeDistance = 17;

      if (arrowPosition === 'top' || arrowPosition === 'bottom') {
        const targetCenterX = targetRect.left + targetRect.width / 2;
        const tooltipRightEdge = tooltipRect.right;
        const tooltipWidth = tooltipRect.width;
        const dynamicOffset = tooltipRightEdge - targetCenterX - 1;
        const clampedOffset = Math.max(minEdgeDistance, Math.min(dynamicOffset, tooltipWidth - minEdgeDistance));
        setArrowOffset(clampedOffset);
      } else {
        const targetCenterY = targetRect.top + targetRect.height / 2;
        const tooltipBottomEdge = tooltipRect.bottom;
        const tooltipHeight = tooltipRect.height;
        const dynamicOffset = tooltipBottomEdge - targetCenterY + 1;
        const clampedOffset = Math.max(minEdgeDistance, Math.min(dynamicOffset, tooltipHeight - minEdgeDistance));
        setArrowOffset(clampedOffset);
      }
    }
  }, [arrowAlign, targetRef, arrowPosition, isOpen]);

  useEffect(() => {
    if (!usePortal || !isOpen || !targetRef?.current) return undefined;

    const updatePosition = () => {
      if (!targetRef.current) return;
      const rect = targetRef.current.getBoundingClientRect();
      const fromRight = window.innerWidth - rect.right;

      switch (position) {
        case 'top':
          setFixedPosition({
            position: 'fixed',
            bottom: window.innerHeight - rect.top + PORTAL_GAP_PX,
            right: fromRight,
          });
          break;
        case 'bottom':
          setFixedPosition({ position: 'fixed', top: rect.bottom + PORTAL_GAP_PX, right: fromRight });
          break;
        case 'left':
          setFixedPosition({ position: 'fixed', top: rect.top, right: window.innerWidth - rect.left + PORTAL_GAP_PX });
          break;
        case 'right':
          setFixedPosition({ position: 'fixed', top: rect.top, left: rect.right + PORTAL_GAP_PX });
          break;
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [usePortal, isOpen, targetRef, position]);

  if (!isOpen) {
    return null;
  }

  const getArrowStyle = (): CSSProperties => {
    if (arrowAlign === 'dynamic' && arrowOffset !== undefined) {
      if (arrowPosition === 'top' || arrowPosition === 'bottom') {
        return { right: `${arrowOffset}px` };
      } else {
        return { bottom: `${arrowOffset}px` };
      }
    }
    return {};
  };

  const tooltipStyle: CSSProperties = {
    minWidth: minWidth ? `${minWidth}px` : undefined,
    ...(usePortal && fixedPosition),
  };

  const tooltip = (
    <div
      ref={tooltipRef}
      className={classNames(
        styles.tooltip,
        styles[`position-${position}`],
        {
          [styles.visible]: isVisible,
        },
        tooltipClassName,
        className,
      )}
      style={tooltipStyle}
    >
      <div
        className={classNames(styles.arrow, styles[`arrow-${arrowPosition}`], styles[`arrow-align-${arrowAlign}`])}
        style={getArrowStyle()}
      />
      <div className={classNames(styles.tooltipContent, contentClassName)}>{children}</div>
    </div>
  );

  return usePortal ? createPortal(tooltip, document.body) : tooltip;
};
